import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  BellRing,
  Camera,
  Loader2,
  MapPin,
  RefreshCw,
  Settings,
  ShieldCheck,
} from "lucide-react";
import {
  OPERATIONAL_PERMISSIONS,
  getOperationalPermissionLabel,
  hasOperationalPermissions,
  markOperationalPermissionsCompleted,
  openOperationalPermissionsSettings,
  requestOperationalPermission,
  requiresOperationalPermissions,
  type OperationalPermissionId,
} from "@/lib/native/operationalPermissions";

interface OperationalPermissionsGateProps {
  children: ReactNode;
}

type GateState = "checking" | "requesting" | "blocked" | "ready";

function PermissionIcon({
  permission,
}: {
  permission: OperationalPermissionId;
}) {
  if (permission === "notifications") return <BellRing size={17} />;
  if (permission === "location") return <MapPin size={17} />;
  return <Camera size={17} />;
}

export function OperationalPermissionsGate({
  children,
}: OperationalPermissionsGateProps) {
  const [state, setState] = useState<GateState>(() =>
    requiresOperationalPermissions() && !hasOperationalPermissions()
      ? "checking"
      : "ready",
  );
  const [activePermission, setActivePermission] =
    useState<OperationalPermissionId | null>(null);
  const [failedPermission, setFailedPermission] =
    useState<OperationalPermissionId | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const startedRef = useRef(false);
  const retryAfterSettingsRef = useRef(false);

  const requestAllPermissions = useCallback(async (): Promise<void> => {
    setState("requesting");
    setFailedPermission(null);
    setCompletedCount(0);

    for (const [index, permission] of OPERATIONAL_PERMISSIONS.entries()) {
      setActivePermission(permission);
      const granted = await requestOperationalPermission(permission);
      if (!granted) {
        setActivePermission(null);
        setFailedPermission(permission);
        setState("blocked");
        return;
      }
      setCompletedCount(index + 1);
    }

    setActivePermission(null);
    markOperationalPermissionsCompleted();
    setState("ready");
  }, []);

  useEffect(() => {
    if (!requiresOperationalPermissions() || hasOperationalPermissions())
      return;
    if (startedRef.current) return;
    startedRef.current = true;
    void requestAllPermissions();
  }, [requestAllPermissions]);

  useEffect(() => {
    const retryAfterSettings = () => {
      if (
        !retryAfterSettingsRef.current ||
        document.visibilityState !== "visible"
      )
        return;
      retryAfterSettingsRef.current = false;
      void requestAllPermissions();
    };
    document.addEventListener("visibilitychange", retryAfterSettings);
    return () =>
      document.removeEventListener("visibilitychange", retryAfterSettings);
  }, [requestAllPermissions]);

  const openSettings = async (): Promise<void> => {
    retryAfterSettingsRef.current = await openOperationalPermissionsSettings();
  };

  if (state === "ready") return <>{children}</>;

  return (
    <main className="flex min-h-dvh items-center justify-center bg-slate-950 px-4 py-8 text-slate-100">
      <section
        className="w-full max-w-lg border border-slate-700 bg-slate-900 p-6 shadow-2xl sm:p-8"
        aria-live="polite"
      >
        <div className="mb-6 flex size-12 items-center justify-center bg-primary-600 text-white">
          <ShieldCheck size={25} />
        </div>
        <p className="text-xs font-bold uppercase text-primary-300">
          Configuración inicial del dispositivo
        </p>
        <h1 className="mt-2 text-2xl font-bold text-white">
          Permisos operativos requeridos
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-300">
          ALTENOT necesita estos accesos para registrar asistencia con
          evidencia, avisarte de eventos operativos y usar las funciones móviles
          del negocio.
        </p>

        <ol className="mt-6 space-y-3">
          {OPERATIONAL_PERMISSIONS.map((permission, index) => {
            const isActive = activePermission === permission;
            const isCompleted = index < completedCount;
            const isFailed = failedPermission === permission;
            return (
              <li
                key={permission}
                className={`flex items-center gap-3 border px-3 py-3 text-sm ${
                  isFailed
                    ? "border-red-500/70 bg-red-950/30 text-red-100"
                    : isCompleted
                      ? "border-emerald-600/60 bg-emerald-950/30 text-emerald-100"
                      : isActive
                        ? "border-primary-500 bg-primary-950/40 text-primary-100"
                        : "border-slate-700 text-slate-300"
                }`}
              >
                <PermissionIcon permission={permission} />
                <span className="flex-1 font-semibold">
                  {getOperationalPermissionLabel(permission)}
                </span>
                {isActive && <Loader2 className="animate-spin" size={17} />}
                {isCompleted && <ShieldCheck size={17} />}
              </li>
            );
          })}
        </ol>

        {state === "blocked" ? (
          <div className="mt-6 border border-red-500/60 bg-red-950/30 p-4">
            <p className="text-sm font-bold text-red-100">
              Falta autorizar{" "}
              {failedPermission
                ? getOperationalPermissionLabel(failedPermission).toLowerCase()
                : "un permiso"}
              .
            </p>
            <p className="mt-1 text-xs leading-5 text-red-100/85">
              La operación queda bloqueada hasta que se concedan todos los
              permisos. Puedes abrir los ajustes del sistema o volver a ejecutar
              la secuencia.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => void requestAllPermissions()}
                className="inline-flex items-center justify-center gap-2 bg-primary-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-primary-500"
              >
                <RefreshCw size={16} /> Reintentar permisos
              </button>
              <button
                type="button"
                onClick={() => void openSettings()}
                className="inline-flex items-center justify-center gap-2 border border-slate-500 px-4 py-2.5 text-sm font-bold text-slate-100 hover:bg-slate-800"
              >
                <Settings size={16} /> Abrir ajustes
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-6 text-xs text-slate-400">
            Solicitando permiso{" "}
            {activePermission
              ? `${completedCount + 1} de ${OPERATIONAL_PERMISSIONS.length}`
              : "del sistema"}
            ...
          </p>
        )}
      </section>
    </main>
  );
}
